import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router"
import { useState } from "react"
import { Page } from "@/components/page"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Typography } from "@/components/ui/typography"
import { useUserType } from "@/hooks/use-user-type"
import { trpc } from "@/lib/trpc"

export const Route = createFileRoute("/_authed/invitation/$token")({
  component: InvitationPage,
})

function InvitationPage() {
  const { token } = useParams({ from: "/_authed/invitation/$token" })
  const navigate = useNavigate()
  const { isProfessional } = useUserType()
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const {
    data: verificationResult,
    isLoading,
    error: verifyError,
  } = trpc.invitations.verify.useQuery(
    { token },
    {
      retry: false,
    },
  )

  const acceptInvitation = trpc.invitations.accept.useMutation({
    onSuccess: () => {
      setSuccessMessage("Invitation accepted successfully!")
      setError(null)
      // Redirect to appropriate dashboard after a brief delay
      setTimeout(() => {
        navigate({
          to: isProfessional ? "/professional/clients" : "/personal/dashboard",
          replace: true,
        })
      }, 2000)
    },
    onError: (error) => {
      setError(error.message)
      setSuccessMessage(null)
    },
  })

  const declineInvitation = trpc.invitations.decline.useMutation({
    onSuccess: () => {
      setSuccessMessage("Invitation declined.")
      setError(null)
      // Redirect to dashboard after a brief delay
      setTimeout(() => {
        navigate({
          to: isProfessional ? "/professional/clients" : "/personal/dashboard",
          replace: true,
        })
      }, 2000)
    },
    onError: (error) => {
      setError(error.message)
      setSuccessMessage(null)
    },
  })

  const handleAccept = () => {
    acceptInvitation.mutate({ token })
  }

  const handleDecline = () => {
    declineInvitation.mutate({ token })
  }

  if (isLoading) {
    return (
      <Page
        headerSlot={
          <Typography as="h1" size="2xl">
            Invitation
          </Typography>
        }
      >
        <div>Loading invitation details...</div>
      </Page>
    )
  }

  if (verifyError || !verificationResult?.valid || !verificationResult?.invitation) {
    return (
      <Page
        headerSlot={
          <Typography as="h1" size="2xl">
            Invalid Invitation
          </Typography>
        }
      >
        <Alert variant="destructive">
          <AlertDescription>
            This invitation is invalid, expired, or has already been processed.
          </AlertDescription>
        </Alert>
      </Page>
    )
  }

  const invitation = verificationResult.invitation

  return (
    <Page
      headerSlot={
        <Typography as="h1" size="2xl">
          Estate Collaboration Invitation
        </Typography>
      }
    >
      <div className="max-w-2xl mx-auto space-y-6">
        {successMessage && (
          <Alert>
            <AlertDescription>{successMessage}</AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>You've been invited to collaborate</CardTitle>
            <CardDescription>
              You have received an invitation to collaborate on an estate planning project.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Typography as="p" size="sm" className="font-medium text-muted-foreground">
                  From
                </Typography>
                <Typography as="p">
                  {invitation.inviterEmail || "Professional Estate Planner"}
                </Typography>
              </div>

              <div>
                <Typography as="p" size="sm" className="font-medium text-muted-foreground">
                  Role
                </Typography>
                <Badge variant="secondary">{invitation.role}</Badge>
              </div>

              {invitation.estateName && (
                <div className="col-span-2">
                  <Typography as="p" size="sm" className="font-medium text-muted-foreground">
                    Estate
                  </Typography>
                  <Typography as="p">{invitation.estateName}</Typography>
                </div>
              )}

              <div>
                <Typography as="p" size="sm" className="font-medium text-muted-foreground">
                  Expires
                </Typography>
                <Typography as="p">
                  {new Date(invitation.expiresAt).toLocaleDateString()}
                </Typography>
              </div>
            </div>

            <div className="pt-4 space-y-3">
              {invitation.role ? (
                <Typography as="p" size="sm" className="text-muted-foreground">
                  By accepting this invitation, you will gain access to collaborate on estate
                  planning documents and information as a {invitation.role.toLowerCase()} user.
                </Typography>
              ) : (
                <Typography as="p" size="sm" className="text-muted-foreground">
                  By accepting this invitation, you will gain access to collaborate on estate
                  planning documents and information as a user.
                </Typography>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={handleAccept}
                  disabled={acceptInvitation.isPending || declineInvitation.isPending}
                  className="flex-1"
                >
                  {acceptInvitation.isPending ? "Accepting..." : "Accept Invitation"}
                </Button>

                <Button
                  variant="outline"
                  onClick={handleDecline}
                  disabled={acceptInvitation.isPending || declineInvitation.isPending}
                  className="flex-1"
                >
                  {declineInvitation.isPending ? "Declining..." : "Decline"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Page>
  )
}
